//#region Imports
// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  useTheme
} from "@mui/material";

// ** Icon Imports
import Icon from "components/icon";

// ** CSS Import
import "./PreviewInvoice.css";

// ** Third-Party Imports
import dayjs from "dayjs";

// ** Logic Imports
import { SendInvoice } from "../logic/SendInvoice";
import { useInvoicePermission } from "../permissions/InvoicePermissions";
import { GetIndividualPayment } from "../logic/GetPayment";
import { GetServiceRequests } from "screens/Requests/logic/GetServiceRequest";
import { SaveCommunicationLog } from "screens/Customers/logic/SaveCommunicationLog";
import { GetContacts } from "screens/Customers/logic/GetContacts";

//#endregion Imports

const PreviewInvoice = ({ open, handleClose, toast, navigate, setLoading, invoiceID, previewData, terms, notes, fetchInvoice, invoiceType, invoiceJobs }) => {
  //#region Permissions
  const { InvoicePermissionProps } = useInvoicePermission();
  const {
    hasInvoiceSendEmailPermission,
  } = InvoicePermissionProps;
  //#endregion Permissions

  //#region States
  const theme = useTheme();
  const [pageNo, setPageNo] = useState(1);
  const [advanceReceived, setAdvanceReceived] = useState(0);
  
  //#endregion States

  //#region Handlers
  async function fetchCustomerID(requestID) {
    try {
      // wait for the promise to resolve
      const serviceRequestDetails = await GetServiceRequests(0, requestID);
      
      // guard against empty array
      if (serviceRequestDetails.length === 0) {
        console.warn('No service requests found');
        return null;
      }
      
      // extract customerID from the first item
      const customerID = serviceRequestDetails[0].customerID;

      const amountPaid = serviceRequestDetails[0].depositAmountPaid;
      const amountUtilized = serviceRequestDetails[0].depositUtilizedAmount;
      const difference = amountPaid - amountUtilized;

      setAdvanceReceived(difference);
      return customerID;
    } catch (err) {
      console.error('Error fetching service requests:', err);
      throw err;
    }
  }

  const handleSendInovice = async () => {
    setLoading(true);
    const response = await SendInvoice(invoiceID, navigate);
    setLoading(false);
    if (!response.isError) {
      toast.success("Invoice sent successfully!", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });

      const customerID = previewData?.customerID;
      const contacts = (await GetContacts(customerID)) ?? [];
      const primaryContact = contacts.find(c => c.primary === true || c.isPrimary === true);
      const contactID = primaryContact?.id ?? 0;

      const communication = {
        communicationLogID: 0,
        customerID: customerID,
        contactID: contactID,
        type: "Email",
        description: "An Invoice was sent via an Email",
        entityID: previewData?.requestID,
        entityType: "Invoice",
        communicationLogTime: dayjs(new Date).format("YYYY-MM-DDTHH:mm:ss"),        
        followUpReminderTime: dayjs(new Date()).add(7, 'day').format("YYYY-MM-DDTHH:mm:ss"),
      }
      const communicationResponse = SaveCommunicationLog(communication);
      // console.log(communicationResponse);
      fetchInvoice(invoiceID)
      handleClose();
    } else {
      toast.error(response?.message, {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
    }
  };

  const pageNext = () => setPageNo(pageNo + 1);
  const pageBack = () => setPageNo(pageNo - 1);

  const parseAmount = (amount) => {
    return  parseFloat(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) 
  };
  //#endregion Handlers

  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #D9D9D9",
          paddingBottom: "20px !important",
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Preview {pageNo}/2
          </Typography>
        </Box>
        <Box>
          <IconButton onClick={handleClose}>
            <Icon icon="tabler:x" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <div class="container">
          {pageNo === 1 && (
            <div class="main">
              <div class="main-info">
                <div class="company-info">
                  <img
                    src="/images/Logo3.png"
                    width="100"
                    height="100"
                    alt="logo"
                  />
                  <div>
                    <h3>{previewData?.companyName}</h3>
                    <p>{previewData?.companyAddress}</p>
                    <p>{previewData?.customerPhone ? `${previewData?.customerPhone} | ` : ''}{previewData?.email || previewData?.customerEmail}</p>
                  </div>
                </div>
                <div class="invoice-info">
                  <h3 style={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#F4F5F6' }}>INV-{previewData?.invoiceID ? previewData.invoiceID : '000'}</h3>
                  <p>Total Amount</p>
                  <h2>
                    {`$ ${parseAmount(previewData?.totalAmount)}`} 
                  </h2>
                </div>
              </div>
              <div class="content">
                <div class="content-detail">
                  <div class="invoice-detail" style={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA', border: '1px solid rgba(208, 212, 241, 0.2)' }}>
                    <div style={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA' }}>
                      <label>Invoice Issue Date</label>
                      <p>{dayjs(new Date).format('MM/DD/YYYY')}</p>
                    </div>
                    <div style={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA' }}>
                      <label>Payment Due Date</label>
                      <p>
                        {previewData?.dueDate ? dayjs(previewData?.dueDate).format('MM/DD/YYYY') : '-'}
                      </p>
                    </div>
                    <div style={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA' }}>
                      <label>Payment Method</label>
                      <p>
                        {previewData?.paymentMethods 
                            ? previewData.paymentMethods
                                .map(method => method === 'Cheque' ? 'Check' : method)
                                .join(', ') 
                            : '-'}
                      </p>
                    </div>
                  </div>
                  <div class="customer-detail" style={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA', border: '1px solid rgba(208, 212, 241, 0.2)' }}>
                    <div style={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA' }}>
                      <label>Customer Name</label>
                      <p>{previewData?.customerName || previewData?.companyName || 'N/A'}</p>
                    </div>
                    <div style={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA' }}>
                      <label>Jobsite</label>
                      <p>{previewData?.jobSiteName || previewData?.jobsiteName}{` - `}{previewData?.jobSiteAddress || previewData?.jobsiteAddress || 'N/A'}</p>
                    </div>
                    <div style={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA' }}>
                      <label>Email</label>
                      <p>{previewData?.email || previewData?.customerEmail || 'N/A'}</p>
                    </div>
                    <div style={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA' }}>
                      <label>Phone</label>
                      <p>{previewData?.customerPhone || 'N/A'}</p>
                    </div>
                    <div style={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA' }}>
                      <label>Billing Address</label>
                      <p>
                        {
                          previewData?.billingAddress_Street1?.trim() ||
                          previewData?.billingAddress?.trim() ||
                          previewData?.companyAddress?.trim() ||
                          previewData?.jobsiteAddress?.trim() ||
                          "N/A"
                        }
                      </p>
                    </div>
                  </div>
                </div>
                {(invoiceType === 'WorkOrder') && (
                  <div class="content-table">
                    <table class="invoice-table">
                      <thead style={{backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA'}}>
                        <tr>
                          <th>Service Name</th>
                          <th>Hours</th>
                          <th>Tax</th>
                          <th>Total Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceJobs?.map((job, index) => (
                          <tr key={job.jobID || index}>
                            <td>{job.serviceName}</td>
                            <td>{job.totalManHours}</td>
                            <td>
                              {previewData.salesTax 
                                ? `$ ${parseAmount(job.totalAmount * (parseFloat(previewData.salesTax) / 100))}`
                                : '$ 0.00'
                              }
                            </td>
                            <td>{`$ ${parseAmount(job.totalAmount)}`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div class="content-summary">
                  <div class="invoice-summary" style={{backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#FAFAFA'}}>
                    <div>
                      <label>Total Amount</label>
                      <p>
                        {`$ ${parseAmount(previewData?.subTotal)}`}
                      </p>
                    </div>
                     {invoiceType === "WorkOrderDeposit" ? (<></>) : (
                      <>
                        <div>
                          <label>Deposit Credit Available</label>
                          <p>{`$ ${parseAmount(advanceReceived)}`}</p>
                        </div>
                        <div>
                          <label>Deposit Credit Applied</label>
                          <p>{`$ ${parseAmount(previewData?.depositUtilizedAmount)}`}</p>
                        </div>
                        <div>
                          <label>Total Discount</label>
                          <p>
                            {`-($  ${parseAmount(previewData?.discountAmount)})`}
                          </p>
                        </div>
                      </>
                    )}
                    <div>
                      <label>Total Tax</label>
                      <p>{`$ ${parseAmount(previewData?.taxAmount)}`}</p>
                    </div>
                    <div id="grand-total">
                      <label>Grand Total</label>
                      <p>{`$ ${parseAmount(previewData?.totalAmount)}`}</p>
                    </div>
                  </div>
                </div>
              </div>
              {notes && notes.length > 0 && (
                <div class="notes">
                  <label>Notes</label>
                  <ul class="notes-list">
                    {
                      notes
                      .map((note, index) => (
                        <li key={index}>
                          {note.description}
                        </li>
                      ))
                    }
                </ul>
                </div>
              )}
            </div>
          )}
          {pageNo === 2 && (
            <>
              <div class="main">
                <h2 class="tnc-title">Terms & Conditions</h2>
                <ul class="tnc-list">
                  {terms && terms.length > 0 && (
                    terms
                    .filter(t => t.isActive)
                    .map((t, index) => (
                      <li key={index}>
                        {t.paymentTerms}
                      </li>
                    ))
                  )}
                </ul>
              </div>
              <div class="footer">
                <div class="copyright">
                  <p>© {dayjs().year()} {previewData?.companyName}. All rights reserved.</p>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px 24px !important",
          boxShadow: "0px -4px 8px rgb(0, 0, 0, 0.1)",
          zIndex: 1,
          // Mobile styles
          "@media (max-width: 600px)": {
            flexDirection: "column",
            gap: 2,
            "& > *": {
              width: "100%"
            }
          }
        }}
        className="dialog-actions-dense"
      >
        <Box>
          <Button 
            variant="contained" 
            color="info" 
            onClick={handleClose}
            sx={{
              "@media (max-width: 600px)": {
                width: "100%"
              }
            }}
          >
            Cancel
          </Button>          
        </Box>
        <Box sx={{ 
          display: "flex", 
          gap: 2, 
          alignItems: "center",
          "@media (max-width: 600px)": {
            flexDirection: "column",
            width: "100%",
            "& .MuiButton-root": {
              width: "100%"
            }
          }
        }}>
          {pageNo > 1 && (
            <Button variant="outlined" color="main" onClick={pageBack}>
              Back
            </Button>
          )}
          {pageNo === 1 && (
            <Button variant="outlined" color="main" onClick={pageNext}>
              Next
            </Button>
          )}
          <Button 
            variant="contained" 
            color="main"
            {...((invoiceID && invoiceID > 0 && hasInvoiceSendEmailPermission ) && {
              onClick: () => handleSendInovice()
            })}
            disabled={!invoiceID || invoiceID <= 0 || !hasInvoiceSendEmailPermission || previewData?.status === 'Cancelled'}
          >
            Send Invoice
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewInvoice;
